/**
 * BoxDetailUUID - Box Detail page accessed via UUID (for QR code scanning)
 * 
 * This component fetches and displays box details using the box's UUID
 * instead of the numeric ID. This ensures QR codes are globally unique
 * and user-specific.
 */

import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useAppNavigation } from '../hooks/useAppNavigation';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import api from '../services/api';
import ROUTES from '../config/routes';

const BoxDetailUUID = () => {
  const navigate = useAppNavigation();
  const { uuid } = useParams();
  const [box, setBox] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBoxByUUID = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const response = await api.get(`/boxes/uuid/${uuid}`);
        setBox(response.data);
        
        // Redirect to the standard box detail page using the box ID
        // This ensures consistent behavior and URL structure
        if (response.data && response.data.id) {
          navigate.to.BOX_DETAIL(response.data.id);
        }
      } catch (err) {
        console.error('Error fetching box by UUID:', err);
        if (err.response?.status === 403) {
          setError('Access denied - this box belongs to another user. Please log in with the correct account.');
        } else if (err.response?.status === 404) {
          setError('Box not found. The QR code may be invalid or the box may have been deleted.');
        } else {
          setError('Failed to load box. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (uuid) {
      fetchBoxByUUID();
    }
  }, [uuid, navigate]);

  if (isLoading) {
    return (
      <Container maxWidth="sm" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={48} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading box...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate.to.APP_HOME()}
        >
          Go to Home
        </Button>
      </Container>
    );
  }

  // If we get here without redirecting, show a loading state
  // (the redirect should happen in useEffect)
  return (
    <Container maxWidth="sm" sx={{ py: 4, textAlign: 'center' }}>
      <CircularProgress size={48} />
      <Typography variant="body1" sx={{ mt: 2 }}>
        Redirecting to box...
      </Typography>
    </Container>
  );
};

export default BoxDetailUUID;
